import React, { FunctionComponent, ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  ButtonStandard,
  ButtonStandardType
} from 'record-it-components/button/ButtonStandard/ButtonStandard';
import {
  CardListItem,
  CardListItemSize
} from 'record-it-components/cardList/CardListItem/CardListItem';
import { CardListItemIconButton } from 'record-it-components/cardList/CardListItemIconButton/CardListItemIconButton';
import { CardListItemTitle } from 'record-it-components/cardList/CardListItemTitle/CardListItemTitle';
import { useEventCallback } from 'record-it-components/hooks/useEventCallback';
import { prepareProps } from 'record-it-components/utils/propUtils';
import { PropsWithoutChildren } from 'record-it-components/utils/utilityTypes';
import { useRequiredWorkTimeEntryToCreate } from '../../../hooks/createWorkTimeEntry/useRequiredWorkTimeEntryToCreate';
import { useTimeTrackingActivityPathName } from '../../../hooks/entity/timeTrackingActivity/useTimeTrackingActivityPathName';
import { createWorkTimeEntrySlice } from '../../../store/slice/createWorkTimeEntry/createWorkTimeEntrySlice';
import { useAppDispatch } from '../../../store/hooks';
import { iconConfigurations } from '../../../classes/icon/iconConfigurations';
import styles from './WorkTimeEntryToCreateActivityWidget.module.css';
import { RoutePaths } from '../../../classes/RoutePaths/RoutePaths';

export type WorkTimeEntryToCreateActivityWidgetProps = PropsWithoutChildren<
  ComponentProps<'div'>
>;

export const WorkTimeEntryToCreateActivityWidget: FunctionComponent<WorkTimeEntryToCreateActivityWidgetProps> =
  React.memo((props) => {
    const { ...remainingProps } = prepareProps({ props, ignoreChildren: true });

    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const workTimeEntryToCreate = useRequiredWorkTimeEntryToCreate();
    const selectedActivityPathName = useTimeTrackingActivityPathName(
      workTimeEntryToCreate.timeTrackingActivityId
    );

    const handleRemoveSelectedActivityClick = useEventCallback(() => {
      dispatch(
        createWorkTimeEntrySlice.actions.setTimeTrackingActivityId(null)
      );
    });

    const handleSelectActivityClick = useEventCallback(() => {
      navigate(RoutePaths.createWorkTimeEntrySelectActivity());
    });

    return (
      <div {...remainingProps}>
        <div className={styles.title}>
          {t('components.workTime.WorkTimeEntryToCreateActivityWidget.title')}
        </div>

        {selectedActivityPathName != null ? (
          <CardListItem
            size={CardListItemSize.Small}
            inline={true}
            data-testid="WorkTimeEntryBreakListItem.SelectedActivity"
          >
            <CardListItemTitle>{selectedActivityPathName}</CardListItemTitle>
            <CardListItemIconButton
              {...iconConfigurations.prefixed.listItem.deleteIcon}
              onClick={handleRemoveSelectedActivityClick}
              data-testid="WorkTimeEntryBreakListItem.RemoveSelectedActivityButton"
            ></CardListItemIconButton>
          </CardListItem>
        ) : null}

        <div className={styles.selectActivityButtonWrapper}>
          <ButtonStandard
            type={ButtonStandardType.Primary}
            onClick={handleSelectActivityClick}
            data-testid="WorkTimeEntryBreakListItem.SelectActivityButton"
          >
            {t(
              'components.workTime.WorkTimeEntryToCreateActivityWidget.selectActivity',
              {
                context: workTimeEntryToCreate.timeTrackingActivityId
                  ? 'activityAlreadySelected'
                  : null
              }
            )}
          </ButtonStandard>
        </div>
      </div>
    );
  });
